import React, { useState } from "react";
import { AlertTriangle, BarChart3 } from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "../ui/button";
import Input from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Loader from "../ui/loader";
import { useSalesReport } from "../../queries/reports.queries";
import {
  CategoryTotal,
  EXPENSE_CATEGORY_LABELS,
  OVERHEAD_CATEGORY_LABELS,
} from "../../types";
import {
  formatAmount,
  formatMonth,
  formatPrice,
  formatShortDate,
  profitColor,
} from "../../lib/utils";

const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  className?: string;
}> = ({ label, value, hint, className }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-2xl font-bold ${className ?? "text-gray-900"}`}>{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const CategoryBreakdown: React.FC<{
  title: string;
  items: CategoryTotal[];
  labels: Record<string, string>;
}> = ({ title, items, labels }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">No entries in this period</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.category} className="flex justify-between text-sm">
              <span className="text-gray-700">{labels[item.category] ?? item.category}</span>
              <span className="font-medium">{formatAmount(item.total)}</span>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

const SalesReport: React.FC = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data, isLoading, isFetching } = useSalesReport({
    from: from || undefined,
    to: to || undefined,
  });

  if (isLoading || !data) {
    return <Loader isSmall />;
  }

  const { totals, months, soldVehicles } = data;
  const chartData = months.map((m) => ({ ...m, label: formatMonth(m.month) }));

  return (
    <div className={`space-y-6 ${isFetching ? "opacity-70" : ""}`}>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label>From</Label>
          <Input
            type="month"
            value={from || data.from}
            max={to || data.to}
            onChange={(e) => setFrom(e.target.value)}
            className="w-44"
          />
        </div>
        <div>
          <Label>To</Label>
          <Input
            type="month"
            value={to || data.to}
            min={from || data.from}
            onChange={(e) => setTo(e.target.value)}
            className="w-44"
          />
        </div>
        {(from || to) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom("");
              setTo("");
            }}
          >
            Last 12 months
          </Button>
        )}
      </div>

      {data.soldWithoutPrice > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {data.soldWithoutPrice} sold vehicle(s) in this period have no sold price and are
          excluded from revenue and profit.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard label="Vehicles Sold" value={String(totals.vehiclesSold)} />
        <StatCard label="Revenue" value={formatPrice(totals.revenue)} />
        <StatCard
          label="Gross Profit"
          value={formatPrice(totals.grossProfit)}
          hint="Revenue - bought price - vehicle expenses"
          className={profitColor(totals.grossProfit)}
        />
        <StatCard
          label="Vehicle Expenses"
          value={formatPrice(totals.monthlyExpenses)}
          hint="All vehicle expenses incurred"
        />
        <StatCard label="Overhead" value={formatPrice(totals.overhead)} />
        <StatCard
          label="Net Profit"
          value={formatPrice(totals.netProfit)}
          hint="Gross profit - overhead"
          className={profitColor(totals.netProfit)}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Monthly Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ left: 16, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis
                  fontSize={12}
                  tickFormatter={(value: number) =>
                    new Intl.NumberFormat("en-MY", { notation: "compact" }).format(value)
                  }
                />
                <Tooltip formatter={(value) => formatAmount(Number(value))} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--chart-1))" />
                <Bar dataKey="monthlyExpenses" name="Vehicle Expenses" fill="hsl(var(--chart-4))" />
                <Bar dataKey="overhead" name="Overhead" fill="hsl(var(--chart-5))" />
                <Line
                  type="monotone"
                  dataKey="netProfit"
                  name="Net Profit"
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Sold</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Cost of Vehicles</TableHead>
                <TableHead className="text-right">Sold Vehicle Expenses</TableHead>
                <TableHead className="text-right">Gross Profit</TableHead>
                <TableHead className="text-right">Monthly Expenses</TableHead>
                <TableHead className="text-right">Overhead</TableHead>
                <TableHead className="text-right">Net Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m) => (
                <TableRow key={m.month}>
                  <TableCell className="whitespace-nowrap">{formatMonth(m.month)}</TableCell>
                  <TableCell className="text-right">{m.vehiclesSold}</TableCell>
                  <TableCell className="text-right">{formatPrice(m.revenue)}</TableCell>
                  <TableCell className="text-right">{formatPrice(m.costOfVehicles)}</TableCell>
                  <TableCell className="text-right">{formatPrice(m.soldVehicleExpenses)}</TableCell>
                  <TableCell className={`text-right ${profitColor(m.grossProfit)}`}>
                    {formatPrice(m.grossProfit)}
                  </TableCell>
                  <TableCell className="text-right">{formatPrice(m.monthlyExpenses)}</TableCell>
                  <TableCell className="text-right">{formatPrice(m.overhead)}</TableCell>
                  <TableCell className={`text-right font-semibold ${profitColor(m.netProfit)}`}>
                    {formatPrice(m.netProfit)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{totals.vehiclesSold}</TableCell>
                <TableCell className="text-right">{formatPrice(totals.revenue)}</TableCell>
                <TableCell className="text-right">{formatPrice(totals.costOfVehicles)}</TableCell>
                <TableCell className="text-right">
                  {formatPrice(totals.soldVehicleExpenses)}
                </TableCell>
                <TableCell className={`text-right ${profitColor(totals.grossProfit)}`}>
                  {formatPrice(totals.grossProfit)}
                </TableCell>
                <TableCell className="text-right">{formatPrice(totals.monthlyExpenses)}</TableCell>
                <TableCell className="text-right">{formatPrice(totals.overhead)}</TableCell>
                <TableCell className={`text-right ${profitColor(totals.netProfit)}`}>
                  {formatPrice(totals.netProfit)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryBreakdown
          title="Vehicle Expenses by Category"
          items={data.expensesByCategory}
          labels={EXPENSE_CATEGORY_LABELS}
        />
        <CategoryBreakdown
          title="Overhead by Category"
          items={data.overheadByCategory}
          labels={OVERHEAD_CATEGORY_LABELS}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Vehicles Sold ({soldVehicles.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {soldVehicles.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No vehicles sold in this period
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sold On</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Plate</TableHead>
                  <TableHead className="text-right">Bought</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead className="text-right">Sold</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {soldVehicles.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="whitespace-nowrap">{formatShortDate(v.sold_at)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {v.year} {v.make} {v.model}
                    </TableCell>
                    <TableCell>{v.numplate}</TableCell>
                    <TableCell className="text-right">{formatPrice(v.price_bought)}</TableCell>
                    <TableCell className="text-right">{formatPrice(v.total_expenses)}</TableCell>
                    <TableCell className="text-right">
                      {v.price_sold != null ? formatPrice(v.price_sold) : "-"}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${profitColor(v.profit)}`}>
                      {v.profit != null ? formatPrice(v.profit) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesReport;
